/**
 * Header stats for the hike-map elevation profile, taken from the planned
 * trails rather than recomputed from the plotted geometry.
 *
 * The routes panel, the hike page and the mobile apps all show the
 * server-computed `ptv2PlannedTrail.elevationGainMeters`, which uses a
 * 6m moving-baseline threshold (see Simplifiable#calculate_total_elevation_gain)
 * so DEM sample noise doesn't inflate the total. Deriving a second figure from
 * the raw coordinates in the chart made the same route report two different
 * climbs; these helpers keep the profile header on the same source of truth.
 *
 * The view filtering mirrors how the chart filters geojson features:
 *  - 'route' — just that route, whatever its style (alt / spur / transport included)
 *  - 'day'   — the main 'route' segments on that day
 *  - anything else ('full') — the main 'route' segments of the whole hike
 */

export interface ActiveView {
  type?: string;
  dayNumber?: number;
  trailId?: string | number | null;
}

/**
 * Planned trails arrive either as `hikesPlannedTrails` join rows (which carry
 * `day` / `position` and wrap the trail) or as bare `ptv2PlannedTrails`.
 */
function resolvePlannedTrail(trail: any): any {
  return trail?.ptv2PlannedTrail || trail;
}

export function plannedTrailsForActiveView(
  plannedTrails: any[],
  activeView: ActiveView | null | undefined
): any[] {
  const trails = Array.isArray(plannedTrails) ? plannedTrails : [];
  const view = activeView || {};

  if (view.type === 'route') {
    return trails.filter((trail) => {
      const id = resolvePlannedTrail(trail)?.id;
      return id != null && String(id) === String(view.trailId);
    });
  }

  if (view.type === 'day') {
    return trails.filter(
      (trail) =>
        trail?.day === view.dayNumber &&
        resolvePlannedTrail(trail)?.routeStyle === 'route'
    );
  }

  return trails.filter((trail) => resolvePlannedTrail(trail)?.routeStyle === 'route');
}

/**
 * Total elevation gain in metres for the view, or `null` when no planned trail
 * in the view carries a value — callers then fall back to computing it from the
 * geometry with `elevationGainFromCoordinates`.
 */
export function elevationGainMetersForActiveView(
  plannedTrails: any[],
  activeView: ActiveView | null | undefined
): number | null {
  let total = 0;
  let found = false;

  plannedTrailsForActiveView(plannedTrails, activeView).forEach((trail) => {
    const gain = resolvePlannedTrail(trail)?.elevationGainMeters;
    if (gain == null) return;
    total += Number(gain) || 0;
    found = true;
  });

  return found ? total : null;
}

/** Total walking time in minutes for the view; 0 when nothing matches. */
export function durationMinutesForActiveView(
  plannedTrails: any[],
  activeView: ActiveView | null | undefined
): number {
  return plannedTrailsForActiveView(plannedTrails, activeView).reduce(
    (sum, trail) => sum + (Number(resolvePlannedTrail(trail)?.calculatedDurationMinutes) || 0),
    0
  );
}
